import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Users, FileText, Lock, Layers, Zap, Download, MessageCircle, ChevronDown } from 'lucide-react';
import SEO from '../components/SEO';
import RelatedPages from '../components/RelatedPages';
import LeadMagnetModal from '../components/LeadMagnetModal';

const fade   = { hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0 } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };

/* ── Datos ──────────────────────────────────────────────────────────────── */

const PILLARS = [
  {
    n: '01',
    icon: Users,
    title: 'Human-in-the-loop por diseño',
    desc: 'Ningún agente ejecuta acciones sensibles —cobros, envíos de datos, cambios de permisos— sin aprobación explícita o fuera de los umbrales predefinidos por el cliente. El sistema frena antes de actuar, no después.',
    color: '#3B82F6',
  },
  {
    n: '02',
    icon: Lock,
    title: 'Auth Links como consentimiento',
    desc: 'El cliente autoriza conexiones sin compartir contraseñas ni credenciales directas. Cada agente accede solo a lo que fue concedido explícitamente en ese momento, nada más.',
    color: '#10B981',
  },
  {
    n: '03',
    icon: FileText,
    title: 'Trazabilidad y auditoría',
    desc: 'Cada acción queda registrada con timestamp, contexto, decisión tomada, herramienta utilizada y resultado obtenido. Todo es visible en el panel de administración en tiempo real.',
    color: '#8B5CF6',
  },
  {
    n: '04',
    icon: Layers,
    title: 'Permisos por rol y por agente',
    desc: 'Un agente comercial no tiene los mismos permisos que uno de operaciones. Los permisos no son globales: son específicos por función, con alcance mínimo necesario y revisión periódica.',
    color: '#F59E0B',
  },
  {
    n: '05',
    icon: ShieldCheck,
    title: 'Aislamiento de contexto',
    desc: 'La memoria operativa está separada por cliente y por rol. Ningún agente de una organización puede acceder al contexto de otra. Sin contaminación cruzada, sin fugas de datos entre workspaces.',
    color: '#06B6D4',
  },
  {
    n: '06',
    icon: Zap,
    title: 'Respuesta ante incidentes',
    desc: 'Revocación granular de tokens y Auth Links desde el panel. Un botón de emergencia corta el acceso de cualquier agente en segundos, sin necesidad de contactar con soporte técnico.',
    color: '#EF4444',
  },
];

const STATS = [
  {
    stat: '73,3%',
    desc: 'de los intentos de acceso inicial empieza por una persona engañada o una cuenta comprometida.',
    source: 'Verizon DBIR 2025',
    color: '#EF4444',
  },
  {
    stat: '29 min',
    desc: 'es el tiempo medio que tarda un atacante en acceder a infraestructuras empresariales una vez dentro.',
    source: 'CrowdStrike 2025',
    color: '#F59E0B',
  },
  {
    stat: '88%',
    desc: 'de las grandes empresas ya experimenta con agentes de IA integrados en procesos de negocio.',
    source: 'Microsoft Work Trend Index 2025',
    color: '#3B82F6',
  },
];

const COMPARISON_ROWS = [
  {
    risk: 'Acceso a datos',
    without: 'Acceso amplio, difícil de revocar',
    with:    'Permisos mínimos, revocables al instante',
    detail: {
      without: 'Un agente sin gobernanza suele recibir credenciales globales (API keys, tokens con scope completo) que le dan acceso a todos los datos del sistema. Si ese agente es comprometido o actúa fuera de contexto, el alcance del daño es ilimitado.',
      with: 'Cada agente recibe exclusivamente el scope necesario para su función (ej.: el agente de cobro solo puede leer el ID de cliente y generar un Payment Intent; no puede modificar contratos ni leer historial de mensajes). Los permisos se revocan individualmente desde el panel en menos de 5 segundos sin afectar al resto del sistema.',
    },
  },
  {
    risk: 'Acciones sensibles',
    without: 'Se ejecutan sin confirmación',
    with:    'Requieren aprobación humana explícita',
    detail: {
      without: 'Sin controles de Human-in-the-loop, el agente ejecuta cobros, envíos masivos o cambios de configuración en cuanto la lógica interna lo decide. Un error de contexto, una alucinación del LLM o un input malicioso pueden provocar acciones irreversibles.',
      with: 'Neuralia369 define umbrales configurables por acción: importe máximo de cobro, número de destinatarios de una campaña o cambios en permisos. Cualquier acción que supere el umbral genera una pausa y solicita aprobación documentada del operador antes de continuar.',
    },
  },
  {
    risk: 'Auditoría',
    without: 'Sin trazabilidad de decisiones',
    with:    'Log completo con timestamp y contexto',
    detail: {
      without: 'La mayoría de agentes de IA solo registran el resultado final (mensaje enviado, pago procesado), sin registrar qué información usaron para decidirlo ni qué herramienta ejecutaron. Esto hace que un incidente sea imposible de reconstruir.',
      with: 'Cada acción escribe un registro inmutable con: timestamp UTC, ID del agente, herramienta llamada, fragmento de contexto utilizado, resultado obtenido y si fue aprobada por humano o no. El log es visible en tiempo real en el Command Center del admin.',
    },
  },
  {
    risk: 'Revocación',
    without: 'Manual, lenta, con riesgo de omisión',
    with:    'Granular y en segundos desde el panel',
    detail: {
      without: 'Revocar el acceso de un agente no gobernado implica localizar y rotar manualmente cada credencial que usa, proceso que puede llevar horas y dejar ventanas de exposición. Si el agente usa credenciales compartidas, revocarlas afecta a todo el sistema.',
      with: 'Cada agente tiene su propio Auth Link y token de acceso. Un clic en "Revocar acceso" invalida ese token específico en tiempo real sin tocar ningún otro agente ni integración. Ideal para situaciones de incidente o cambio de proveedor.',
    },
  },
  {
    risk: 'Permisos',
    without: 'Globales para todo el sistema',
    with:    'Por rol, por agente y por acción',
    detail: {
      without: 'Los sistemas sin arquitectura de permisos granular asignan un rol único (ej.: "admin" o "editor") que da acceso a funciones no relacionadas con la tarea del agente. El principio de mínimo privilegio queda ignorado por diseño.',
      with: 'La matriz de permisos de Neuralia369 opera en tres niveles: rol del usuario (cliente, operador, admin), tipo de agente (comercial, cobro, soporte…) y acción específica (leer, escribir, enviar, cobrar). Ningún nivel puede elevar privilegios sin aprobación del siguiente.',
    },
  },
];

/* ── Componente ─────────────────────────────────────────────────────────── */

export default function SeguridadAgentesPage() {
  const navigate = useNavigate();
  const [expandedRow, setExpandedRow] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  return (
    <>
      <LeadMagnetModal open={openModal} onClose={() => setOpenModal(false)} source="seguridad-agentes" />
      <SEO
        title="Seguridad de Agentes de IA | Neuralia369"
        description="Tus agentes tienen acceso a lo que tú les autorizas. Ni más, ni menos. Human-in-the-loop, auditoría completa y control granular de permisos en cada agente."
        path="/seguridad-agentes"
      />

      <main className="min-h-screen bg-background" data-testid="seguridad-agentes-page">

        {/* ── HERO ──────────────────────────────────────────────────────── */}
        <section className="relative px-4 pt-28 pb-24 sm:px-6 lg:px-8 overflow-hidden" data-testid="hero-section">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full"
              style={{ background: 'radial-gradient(ellipse, rgba(239,68,68,0.07) 0%, transparent 68%)' }} />
          </div>

          <div className="relative mx-auto max-w-4xl">
            <motion.div initial="hidden" animate="show" variants={stagger}>
              <motion.p variants={fade} className="editorial-kicker mb-6">
                Seguridad por diseño
              </motion.p>

              <motion.h1
                variants={fade}
                className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-text-heading leading-[1.08] tracking-tight"
                data-testid="hero-title"
              >
                Tus agentes tienen acceso a lo que tú les autorizas. Ni más, ni menos. Con auditoría completa, consentimiento explícito y control humano en cada punto crítico.
              </motion.h1>

              <motion.div variants={fade} className="mt-10 flex flex-wrap gap-4">
                <button
                  onClick={() => document.getElementById('pilares')?.scrollIntoView({ behavior: 'smooth' })}
                  className="btn-primary px-8 py-3.5 text-base"
                  data-testid="hero-pillars-btn"
                >
                  Ver los 6 pilares
                </button>
                <button
                  onClick={() => document.getElementById('cta')?.scrollIntoView({ behavior: 'smooth' })}
                  className="btn-secondary px-8 py-3.5 text-base"
                  data-testid="hero-cta-btn"
                >
                  Hablar con un arquitecto
                </button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ── 6 PILARES ──────────────────────────────────────────────────── */}
        <section id="pilares" className="px-4 py-20 sm:px-6 lg:px-8 border-t border-border-subtle" data-testid="pillars-section">
          <div className="mx-auto max-w-7xl">
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}
              className="mb-14">
              <motion.p variants={fade} className="editorial-kicker mb-4">Arquitectura de seguridad</motion.p>
              <motion.h2 variants={fade} className="text-base sm:text-lg font-heading font-semibold text-text-heading max-w-2xl">
                Los 6 pilares de seguridad de Neuralia369
              </motion.h2>
            </motion.div>

            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {PILLARS.map((p, i) => {
                const Icon = p.icon;
                return (
                  <motion.div key={i} variants={fade}
                    className="glass-card p-6 space-y-4 group hover:border-opacity-50 transition-all"
                    style={{ '--hover-color': p.color }}
                    data-testid={`pillar-card-${i}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: `${p.color}18`, border: `1px solid ${p.color}30` }}>
                        <Icon className="w-4 h-4" style={{ color: p.color }} />
                      </div>
                      <span className="text-xs font-mono text-text-muted">{p.n}</span>
                    </div>
                    <p className="text-sm font-semibold text-text-heading leading-snug">{p.title}</p>
                    <p className="text-xs text-text-muted leading-relaxed">{p.desc}</p>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>

        {/* ── DATOS VERIFICADOS ────────────────────────────────────────────── */}
        <section className="px-4 py-20 sm:px-6 lg:px-8 border-t border-border-subtle" data-testid="stats-section">
          <div className="mx-auto max-w-7xl">
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}
              className="mb-14">
              <motion.p variants={fade} className="editorial-kicker mb-4">Contexto de amenazas</motion.p>
              <motion.h2 variants={fade} className="text-base sm:text-lg font-heading font-semibold text-text-heading max-w-xl">
                Los datos que justifican gobernar los agentes desde el primer día.
              </motion.h2>
            </motion.div>

            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}
              className="grid sm:grid-cols-3 gap-5">
              {STATS.map((s, i) => (
                <motion.div key={i} variants={fade}
                  className="rounded-2xl border p-7 space-y-3"
                  style={{ borderColor: `${s.color}30`, background: `${s.color}08` }}
                  data-testid={`stat-card-${i}`}>
                  <p className="text-4xl font-heading font-black" style={{ color: s.color }}>{s.stat}</p>
                  <p className="text-sm text-text-body leading-relaxed">{s.desc}</p>
                  <p className="text-[10px] text-text-muted font-medium uppercase tracking-wider pt-1">
                    Fuente: {s.source}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── TABLA COMPARATIVA ────────────────────────────────────────────── */}
        <section className="px-4 py-20 sm:px-6 lg:px-8 border-t border-border-subtle" data-testid="comparison-section">
          <div className="mx-auto max-w-5xl">
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}
              className="mb-12">
              <motion.p variants={fade} className="editorial-kicker mb-4">Comparativa</motion.p>
              <motion.h2 variants={fade} className="text-base sm:text-lg font-heading font-semibold text-text-heading">
                Agente sin gobernanza vs. Neuralia369
              </motion.h2>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="overflow-x-auto rounded-2xl border border-border-subtle">
              <table className="w-full text-sm" data-testid="comparison-table">
                <thead>
                  <tr className="border-b border-border-subtle">
                    <th className="text-left p-4 text-text-muted text-xs font-semibold uppercase tracking-wider w-1/4">Riesgo</th>
                    <th className="text-left p-4 text-red-400 text-xs font-semibold uppercase tracking-wider w-[37.5%]">
                      Agente sin gobernanza
                    </th>
                    <th className="text-left p-4 text-emerald-400 text-xs font-semibold uppercase tracking-wider w-[37.5%]">
                      Neuralia369
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row, i) => {
                    const isOpen = expandedRow === i;
                    return (
                      <React.Fragment key={i}>
                        <tr
                          className={`border-b border-border-subtle cursor-pointer select-none transition-colors ${
                            i % 2 === 0 ? 'bg-white/[0.01]' : ''
                          } ${isOpen ? 'bg-primary/5' : 'hover:bg-white/[0.03]'}`}
                          onClick={() => setExpandedRow(isOpen ? null : i)}
                          data-testid={`comparison-row-${i}`}
                        >
                          <td className="p-4 text-text-heading font-medium text-xs">
                            <div className="flex items-center gap-2">
                              <ChevronDown
                                className={`w-3.5 h-3.5 text-text-muted flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                              />
                              {row.risk}
                            </div>
                          </td>
                          <td className="p-4 text-red-400/80 text-xs leading-relaxed">{row.without}</td>
                          <td className="p-4 text-emerald-400 text-xs leading-relaxed">
                            <span className="flex items-start gap-1.5">
                              <span className="mt-0.5 flex-shrink-0">✓</span>{row.with}
                            </span>
                          </td>
                        </tr>
                        <AnimatePresence initial={false}>
                          {isOpen && (
                            <tr data-testid={`comparison-detail-${i}`}>
                              <td colSpan={3} className="p-0 border-b border-border-subtle">
                                <motion.div
                                  key="detail"
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.22, ease: 'easeInOut' }}
                                  className="overflow-hidden"
                                >
                                  <div className="grid sm:grid-cols-2 gap-4 px-5 py-4 bg-background/60">
                                    <div className="space-y-1.5">
                                      <p className="text-[10px] font-semibold uppercase tracking-wider text-red-400">Sin gobernanza</p>
                                      <p className="text-xs text-text-muted leading-relaxed">{row.detail.without}</p>
                                    </div>
                                    <div className="space-y-1.5">
                                      <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400">Neuralia369</p>
                                      <p className="text-xs text-text-body leading-relaxed">{row.detail.with}</p>
                                    </div>
                                  </div>
                                </motion.div>
                              </td>
                            </tr>
                          )}
                        </AnimatePresence>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </motion.div>
          </div>
        </section>

        {/* ── CUMPLIMIENTO ─────────────────────────────────────────────────── */}
        <section className="px-4 py-20 sm:px-6 lg:px-8 border-t border-border-subtle" data-testid="compliance-section">
          <div className="mx-auto max-w-7xl">
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}
              className="mb-12">
              <motion.p variants={fade} className="editorial-kicker mb-4">Marcos de referencia</motion.p>
              <motion.h2 variants={fade} className="text-base sm:text-lg font-heading font-semibold text-text-heading max-w-xl">
                Diseñado con los estándares de la industria como referencia.
              </motion.h2>
            </motion.div>

            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}
              className="grid sm:grid-cols-3 gap-5">
              {[
                {
                  badge: 'Alineado',
                  badgeColor: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
                  title: 'OWASP Top 10 para LLM',
                  desc: 'El diseño de Neuralia369 toma como referencia el Top 10 de riesgos de seguridad en aplicaciones de IA de OWASP (LLM01–LLM10), incluyendo prompt injection, exceso de permisos y manejo inseguro de salidas.',
                },
                {
                  badge: 'Referencia de diseño',
                  badgeColor: 'bg-blue-500/15 text-blue-400 border-blue-500/25',
                  title: 'NIST AI RMF',
                  desc: 'La arquitectura de gobernanza de agentes sigue los principios del Marco de Gestión de Riesgos de IA del NIST (Govern, Map, Measure, Manage) como guía de diseño para la toma de decisiones sensibles.',
                },
                {
                  badge: 'En camino',
                  badgeColor: 'bg-amber-500/15 text-amber-400 border-amber-500/25',
                  title: 'SOC 2 Type II',
                  desc: 'Neuralia369 tiene en hoja de ruta la obtención de la certificación SOC 2 Type II. La infraestructura y los controles actuales están siendo diseñados para facilitar ese proceso.',
                },
              ].map((c, i) => (
                <motion.div key={i} variants={fade} className="glass-card p-6 space-y-4" data-testid={`compliance-card-${i}`}>
                  <span className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border ${c.badgeColor}`}>
                    {c.badge}
                  </span>
                  <p className="text-sm font-semibold text-text-heading">{c.title}</p>
                  <p className="text-xs text-text-muted leading-relaxed">{c.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── CTA DOBLE ────────────────────────────────────────────────────── */}
        <section id="cta" className="px-4 py-24 sm:px-6 lg:px-8 border-t border-border-subtle" data-testid="cta-section">
          <div className="mx-auto max-w-4xl">
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}
              className="glass-card tracing-panel p-8 sm:p-12">
              <div className="grid lg:grid-cols-2 gap-10 items-center">
                <div className="space-y-4">
                  <motion.p variants={fade} className="editorial-kicker">Siguiente paso</motion.p>
                  <motion.h2 variants={fade} className="text-base sm:text-lg font-heading font-semibold text-text-heading">
                    ¿Quieres revisar la seguridad de tu operación con agentes?
                  </motion.h2>
                  <motion.p variants={fade} className="text-xs text-text-muted leading-relaxed">
                    Evaluamos tu arquitectura actual, identificamos los puntos de exposición y definimos los controles necesarios antes de activar cualquier agente.
                  </motion.p>
                </div>

                <motion.div variants={stagger} className="flex flex-col gap-4">
                  <motion.button
                    variants={fade}
                    onClick={() => setOpenModal(true)}
                    className="btn-secondary py-4 px-6 text-sm flex items-center justify-center gap-2.5 w-full"
                    data-testid="cta-checklist-btn"
                  >
                    <Download className="w-4 h-4 flex-shrink-0" />
                    Descargar checklist de seguridad para agentes de IA
                  </motion.button>

                  <motion.button
                    variants={fade}
                    onClick={() => navigate('/activar')}
                    className="btn-primary py-4 px-6 text-sm flex items-center justify-center gap-2.5 w-full"
                    data-testid="cta-architect-btn"
                  >
                    <MessageCircle className="w-4 h-4 flex-shrink-0" />
                    Hablar con un arquitecto de seguridad
                  </motion.button>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── NOTA AL PIE ──────────────────────────────────────────────────── */}
        <div className="px-4 pb-16 sm:px-6 lg:px-8" data-testid="footer-note">
          <div className="mx-auto max-w-7xl border-t border-border-subtle pt-6">
            <p className="text-[11px] text-text-muted">
              Última actualización: octubre 2026. Esta página se revisa mensualmente.
            </p>
          </div>
        </div>

      </main>
      <RelatedPages pages={['sistema-multiagente','soluciones','developers','recursos']} />
    </>
  );
}
